import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CoachMarkService } from "../coach-mark.service";
import { FeatureFlagService } from "../feature-flag.service";
import { InteractionTrackingService } from "../interaction-tracking.service";
import { NovaCompanionSettingsService } from "../nova-companion-settings.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { LearnOrientationPrompt } from "./learn-orientation-lesson";
import { NovaChatLesson } from "./nova-chat-lesson";

export const N04_LESSON_ID = "N04";

/** Recorded with N04's completed lesson state - N04's CompletedAt *is* this milestone. */
export const NOVA_BUFFER_HEADER_INTRODUCED = "nova_buffer_header_introduced";

/** Anchors workbook: the coloured Nova header. */
export const N04_ANCHOR = "nova.buffer_header";

/** Copy rows N04.01-N04.02, workbook v2.38 Copy sheet (verbatim). */
export const N04_COPY = {
  heading: "Your Safety Buffer",
  body:
    "The top of Nova's chat box reflects your current Safety Buffer. This gives Nova useful context for " +
    "your conversation, but you can talk about whatever feels important to you.",
};

/**
 * Onboarding lesson N04 "Your Safety Buffer (Nova header)" (FLIP-392; spec v2.36 §24.5, workbook
 * v2.38): an informational anchored prompt at the coloured Nova header. Flow shared with the other
 * chat lessons - see NovaChatLesson.
 *
 * - Driven by the chat's state: the chat calls `setReady(true)` once the header is coloured (a Score
 *   exists today - the buffer band is green / amber / red), N02's first-use panel is closed and N03A
 *   and N03B are done - the last of the Nova sequence, so only one prompt shows at a time - and
 *   `setReady(false)` when any of that stops being true.
 * - While the header is the neutral cream (no Score) it never shows - it is deferred, not skipped, so
 *   the first Nova opening once a coloured header is available shows it. Nova is not opened for it.
 * - The header is not a control, so the only way to complete is the visible close (standard
 *   informational dismissal, permanent). Closing Nova with the prompt up records nothing and it
 *   shows again next time.
 */
@Injectable({ providedIn: "root" })
export class N04SafetyBufferHeaderService extends NovaChatLesson {
  protected readonly stepKey = N04_LESSON_ID;
  protected readonly milestone = NOVA_BUFFER_HEADER_INTRODUCED;

  constructor(
    router: Router,
    coachMarkService: CoachMarkService,
    blockingUiSignal: BlockingUiSignalService,
    featureFlagService: FeatureFlagService,
    paymentsService: PaymentsService,
    onboardingStepService: OnboardingStepService,
    interactionTracking: InteractionTrackingService,
    companionSettings: NovaCompanionSettingsService
  ) {
    super(
      router,
      coachMarkService,
      blockingUiSignal,
      featureFlagService,
      paymentsService,
      onboardingStepService,
      interactionTracking,
      companionSettings
    );
  }

  protected prompt(): LearnOrientationPrompt | null {
    return this.companionOn ? { lessonId: N04_LESSON_ID, target: N04_ANCHOR, ...N04_COPY } : null;
  }
}
